import React from 'react';
import { useStore } from '../../../../../../stores/root';

// assets

// services

// props
import { IOrderContainerGoodsResourceShortProps } from '../../../../../../props/orders/containers/goods';

// components
import { IColumn, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../typography/text';
import GeneralService from '../../../../../../services/general';

interface TOrderContainerGoodsListProps {
    orderId: string;
    containerId: string;
    qs?: string[];
    goods?: IOrderContainerGoodsResourceShortProps[];
    columns?: TOrderContainerGoodsListColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TOrderContainerGoodsListColumn = 'idx' | 'goods' | 'quantity' | 'size' | 'volume' | 'weight' | 'totalWeight';

const OrderContainerGoodsList: React.FC<TOrderContainerGoodsListProps> = (props: TOrderContainerGoodsListProps) => {
    const shownColumns: TOrderContainerGoodsListColumn[] = props.columns || [
        'idx',
        'goods',
        'quantity',
        'size',
        'volume',
        'weight',
        'totalWeight'
    ];

    const { banner } = useStore();
    const [goods, setGoods] = React.useState<IOrderContainerGoodsResourceShortProps[]>(props.goods || []);

    const columns: IColumn[] = [
        {
            key: "idx",
            name: "No.",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item: IOrderContainerGoodsResourceShortProps, idx?: number) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{(idx || 0) + 1}</Text>
                </Stack>
            }
        },
        {
            key: "goods",
            name: "Goods",
            minWidth: 150,
            onRender: (item: IOrderContainerGoodsResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{item.goods.name}</Text>
                </Stack>
            }
        },
        {
            key: "quantity",
            name: "Quantity",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: IOrderContainerGoodsResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{item.quantityAmount} {item.goods.quantityUnit}</Text>
                </Stack>
            }
        },
        {
            key: "size",
            name: "Size",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IOrderContainerGoodsResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{GeneralService.getNumberWithSeparator(Number(item.length))}cm x {GeneralService.getNumberWithSeparator(Number(item.width))}cm x {GeneralService.getNumberWithSeparator(Number(item.height))}cm</Text>
                </Stack>
            }
        },
        {
            key: "volume",
            name: "Volume (m3)",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: IOrderContainerGoodsResourceShortProps) => {
                const volume = GeneralService.calculateVolume(item.length + "", item.width + "", item.height + "");

                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {volume > 0 ? <Text>{GeneralService.getNumberWithSeparator(volume)} m<sup>3</sup></Text> : null}
                    {volume <= 0 ? <Text>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "weight",
            name: "Weight",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: IOrderContainerGoodsResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{item.quantityAmount} {item.weightUnit}</Text>
                </Stack>
            }
        },
        {
            key: "totalWeight",
            name: "Total Weight",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: IOrderContainerGoodsResourceShortProps) => {
                const weight = Number(item.weightAmount);
                const quantity = Number(item.quantityAmount);
                const totalWeight = weight * quantity;

                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    {totalWeight > 0 ? <Text>{GeneralService.getNumberWithSeparator(totalWeight)} kg</Text> : null}
                    {totalWeight <= 0 ? <Text>-</Text> : null}
                </Stack>
            }
        }
    ];

    return <Stack>
        {goods.length > 0 ? <ShimmeredDetailsList
            setKey="items"
            items={goods}
            columns={columns.filter((col) => shownColumns.indexOf(col.key as TOrderContainerGoodsListColumn) > -1)}
            selectionMode={SelectionMode.none}
            enableShimmer={false}
            onShouldVirtualize={() => false}
            ariaLabelForShimmer="Content is being fetched"
            ariaLabelForGrid="Item details" /> : null}
        {goods.length <= 0 ? <Text size={'small'}>Goods not found</Text> : null}
    </Stack>;
};

export default OrderContainerGoodsList;
